
import { PythonModule } from './types';

// Additional modules for Python Essentials track to complete the fundamentals
export const PYTHON_EXCEPTION_HANDLING_MODULE: PythonModule = {
  id: "python-exception-handling",
  title: "Exception Handling and Error Management",
  description: "Master Python's exception handling mechanisms to write robust, error-resistant code.",
  difficulty: "beginner",
  content: `
# Exception Handling and Error Management

Exception handling is crucial for writing robust Python applications that can gracefully handle unexpected situations.

## Understanding Exceptions

Exceptions are events that occur during program execution that disrupt the normal flow of instructions.

### Common Exception Types

\`\`\`python
# ZeroDivisionError
try:
    result = 10 / 0  # Raises ZeroDivisionError
except ZeroDivisionError as e:
    print(f"Error: {e}")

# TypeError
try:
    number = "5" + 5  # Raises TypeError
except TypeError as e:
    print(f"Error: {e}")

# ValueError
try:
    number = int("hello")  # Raises ValueError
except ValueError as e:
    print(f"Error: {e}")

# IndexError
try:
    my_list = [1, 2, 3]
    print(my_list[5])  # Raises IndexError
except IndexError as e:
    print(f"Error: {e}")

# KeyError
try:
    my_dict = {"name": "Alice"}
    print(my_dict["age"])  # Raises KeyError
except KeyError as e:
    print(f"Error: {e}")
\`\`\`

## Try-Except Blocks

\`\`\`python
# Basic try-except
try:
    result = 10 / 0
except ZeroDivisionError:
    print("Cannot divide by zero!")
    result = 0

# Handling multiple exceptions
try:
    user_input = input("Enter a number: ")
    number = int(user_input)
    result = 10 / number
except ValueError:
    print("Invalid number format!")
except ZeroDivisionError:
    print("Cannot divide by zero!")
except Exception as e:
    print(f"An unexpected error occurred: {e}")
\`\`\`

## Finally and Else Clauses

\`\`\`python
def safe_file_operation(filename):
    try:
        file = open(filename, 'r')
        content = file.read()
    except FileNotFoundError:
        print(f"File {filename} not found!")
        return None
    except PermissionError:
        print(f"Permission denied to read {filename}")
        return None
    else:
        print("File read successfully!")
        return content
    finally:
        # This always executes
        if 'file' in locals() and not file.closed:
            file.close()
            print("File closed.")
\`\`\`

## Raising Custom Exceptions

\`\`\`python
class InvalidAgeError(Exception):
    """Custom exception for invalid age values"""
    def __init__(self, age, message="Age must be between 0 and 150"):
        self.age = age
        self.message = message
        super().__init__(self.message)

def validate_age(age):
    if age < 0 or age > 150:
        raise InvalidAgeError(age)
    return True

# Using custom exception
try:
    validate_age(-5)
except InvalidAgeError as e:
    print(f"Invalid age: {e.age}. {e.message}")
\`\`\`

## Best Practices

1. **Be Specific**: Catch specific exceptions rather than using bare \`except\`
2. **Use Finally for Cleanup**: Always clean up resources
3. **Log Errors**: Keep track of errors for debugging
4. **Fail Fast**: Don't hide errors unnecessarily

## Practice Exercise

Create a calculator function that handles all possible errors gracefully and provides meaningful error messages to users.
`,
  duration: "50 minutes",
  objectives: [
    "Understand different types of Python exceptions",
    "Master try-except-else-finally blocks",
    "Create and raise custom exceptions",
    "Implement proper error handling in real applications",
    "Apply exception handling best practices"
  ],
  order: 6
};

export const PYTHON_FILE_OPERATIONS_MODULE: PythonModule = {
  id: "python-file-operations",
  title: "File Operations and Data Persistence",
  description: "Learn to work with files, directories, and persistent data storage in Python.",
  difficulty: "beginner",
  content: `
# File Operations and Data Persistence

Working with files is essential for data persistence, configuration management, and data processing in Python applications.

## File Basics

### Opening and Closing Files

\`\`\`python
# Basic file operations
file = open('example.txt', 'r')
content = file.read()
file.close()

# Better approach with context manager
with open('example.txt', 'r') as file:
    content = file.read()
# File automatically closes when exiting the with block
\`\`\`

### File Modes

\`\`\`python
# Read modes
with open('file.txt', 'r') as f:    # Read text
    content = f.read()

with open('file.txt', 'rb') as f:   # Read binary
    binary_data = f.read()

# Write modes
with open('file.txt', 'w') as f:    # Write (overwrites)
    f.write("New content")

with open('file.txt', 'a') as f:    # Append
    f.write("Additional content")

# Read and write
with open('file.txt', 'r+') as f:   # Read and write
    content = f.read()
    f.write("More content")
\`\`\`

## Reading File Content

\`\`\`python
# Reading entire file
with open('data.txt', 'r') as file:
    content = file.read()

# Reading line by line
with open('data.txt', 'r') as file:
    for line in file:
        print(line.strip())

# Reading all lines into a list
with open('data.txt', 'r') as file:
    lines = file.readlines()

# Reading specific number of characters
with open('data.txt', 'r') as file:
    chunk = file.read(100)  # Read first 100 characters
\`\`\`

## Writing to Files

\`\`\`python
# Writing strings
with open('output.txt', 'w') as file:
    file.write("Hello, World!\\n")
    file.write("Second line\\n")

# Writing multiple lines
lines = ["Line 1\\n", "Line 2\\n", "Line 3\\n"]
with open('output.txt', 'w') as file:
    file.writelines(lines)

# Using print function for file output
with open('output.txt', 'w') as file:
    print("Hello, World!", file=file)
    print("Another line", file=file)
\`\`\`

## Working with CSV Files

\`\`\`python
import csv

# Writing CSV
data = [
    ['Name', 'Age', 'City'],
    ['Alice', 30, 'New York'],
    ['Bob', 25, 'San Francisco'],
    ['Charlie', 35, 'Chicago']
]

with open('people.csv', 'w', newline='') as file:
    writer = csv.writer(file)
    writer.writerows(data)

# Reading CSV
with open('people.csv', 'r') as file:
    reader = csv.reader(file)
    for row in reader:
        print(row)

# Using DictReader for named columns
with open('people.csv', 'r') as file:
    reader = csv.DictReader(file)
    for row in reader:
        print(f"Name: {row['Name']}, Age: {row['Age']}")
\`\`\`

## Working with JSON

\`\`\`python
import json

# Writing JSON
data = {
    "name": "Alice",
    "age": 30,
    "hobbies": ["reading", "swimming", "coding"],
    "address": {
        "city": "New York",
        "state": "NY"
    }
}

with open('data.json', 'w') as file:
    json.dump(data, file, indent=2)

# Reading JSON
with open('data.json', 'r') as file:
    loaded_data = json.load(file)
    print(loaded_data['name'])
\`\`\`

## Directory Operations

\`\`\`python
import os
import glob

# Current directory
current_dir = os.getcwd()
print(f"Current directory: {current_dir}")

# List directory contents
files = os.listdir('.')
print("Files in current directory:", files)

# Check if path exists
if os.path.exists('myfile.txt'):
    print("File exists")

# Create directory
os.makedirs('new_folder', exist_ok=True)

# File path operations
filepath = os.path.join('folder', 'subfolder', 'file.txt')
dirname = os.path.dirname(filepath)
basename = os.path.basename(filepath)

# Finding files with patterns
python_files = glob.glob('*.py')
all_txt_files = glob.glob('**/*.txt', recursive=True)
\`\`\`

## Path Operations with pathlib

\`\`\`python
from pathlib import Path

# Modern way to work with paths
current_dir = Path.cwd()
home_dir = Path.home()

# Creating paths
file_path = Path('data') / 'files' / 'example.txt'

# Path properties
print(file_path.name)        # example.txt
print(file_path.stem)        # example
print(file_path.suffix)      # .txt
print(file_path.parent)      # data/files

# Path operations
if file_path.exists():
    content = file_path.read_text()
    
file_path.write_text("New content")

# Directory operations
data_dir = Path('data')
data_dir.mkdir(exist_ok=True)

# Iterating over files
for txt_file in data_dir.glob('*.txt'):
    print(txt_file)
\`\`\`

## Best Practices

1. **Always use context managers** (\`with\` statements) for file operations
2. **Handle exceptions** when working with files
3. **Specify encoding** explicitly when working with text files
4. **Use pathlib** for modern path operations
5. **Close files properly** to avoid resource leaks

## Practice Exercise

Create a simple log file manager that can write log entries with timestamps, read recent entries, and manage log file rotation.
`,
  duration: "55 minutes",
  objectives: [
    "Master file opening, reading, and writing operations",
    "Work with different file formats (text, CSV, JSON)",
    "Understand file modes and context managers",
    "Perform directory and path operations",
    "Apply file handling best practices and error handling"
  ],
  order: 7
};

export const PYTHON_PROJECT_ORGANIZATION_MODULE: PythonModule = {
  id: "python-project-organization",
  title: "Project Organization and Code Structure",
  description: "Learn to organize Python projects, create modules, packages, and follow best practices for maintainable code.",
  difficulty: "beginner",
  content: `
# Project Organization and Code Structure

Proper project organization is crucial for maintainable, scalable Python applications.

## Python Modules

A module is a file containing Python code. Any Python file can be imported as a module.

### Creating Modules

\`\`\`python
# math_utils.py
def add(a, b):
    """Add two numbers"""
    return a + b

def multiply(a, b):
    """Multiply two numbers"""
    return a * b

def calculate_area(radius):
    """Calculate circle area"""
    import math
    return math.pi * radius ** 2

# Constants
PI = 3.14159
GRAVITY = 9.81
\`\`\`

### Using Modules

\`\`\`python
# main.py
import math_utils

result = math_utils.add(5, 3)
area = math_utils.calculate_area(10)

# Alternative import styles
from math_utils import add, multiply
result = add(5, 3)

from math_utils import calculate_area as calc_area
area = calc_area(10)

import math_utils as math
result = math.add(5, 3)
\`\`\`

## Python Packages

A package is a directory containing multiple modules with an \`__init__.py\` file.

### Package Structure

\`\`\`
my_project/
    __init__.py
    main.py
    utils/
        __init__.py
        math_utils.py
        string_utils.py
    data/
        __init__.py
        processors.py
        validators.py
    tests/
        __init__.py
        test_math_utils.py
        test_validators.py
\`\`\`

### Package __init__.py

\`\`\`python
# utils/__init__.py
"""Utility functions package"""

from .math_utils import add, multiply
from .string_utils import capitalize_words

__version__ = "1.0.0"
__all__ = ["add", "multiply", "capitalize_words"]
\`\`\`

### Using Packages

\`\`\`python
# Import from package
from utils import add, multiply
from utils.string_utils import clean_text
from data.processors import process_csv

# Import entire package
import utils
result = utils.add(5, 3)
\`\`\`

## Project Structure Best Practices

### Small Project Structure

\`\`\`
simple_calculator/
    calculator.py
    utils.py
    main.py
    README.md
    requirements.txt
\`\`\`

### Medium Project Structure

\`\`\`
web_scraper/
    src/
        web_scraper/
            __init__.py
            scraper.py
            parser.py
            utils.py
    tests/
        test_scraper.py
        test_parser.py
    docs/
        README.md
        usage.md
    requirements.txt
    setup.py
    .gitignore
\`\`\`

### Large Project Structure

\`\`\`
e_commerce_api/
    src/
        ecommerce/
            __init__.py
            models/
                __init__.py
                user.py
                product.py
                order.py
            services/
                __init__.py
                user_service.py
                product_service.py
                payment_service.py
            api/
                __init__.py
                routes.py
                middleware.py
            utils/
                __init__.py
                validators.py
                helpers.py
    tests/
        unit/
        integration/
    docs/
    config/
        development.py
        production.py
    requirements/
        base.txt
        development.txt
        production.txt
    setup.py
    README.md
    .gitignore
    Dockerfile
\`\`\`

## Virtual Environments

Virtual environments isolate project dependencies.

### Creating Virtual Environment

\`\`\`bash
# Using venv (Python 3.3+)
python -m venv myproject_env

# Activate (Windows)
myproject_env\\Scripts\\activate

# Activate (macOS/Linux)
source myproject_env/bin/activate

# Deactivate
deactivate
\`\`\`

### Managing Dependencies

\`\`\`bash
# Install packages
pip install requests pandas numpy

# Create requirements file
pip freeze > requirements.txt

# Install from requirements
pip install -r requirements.txt

# Install development dependencies
pip install -e .  # Install package in development mode
\`\`\`

## Code Organization Principles

### 1. Separation of Concerns

\`\`\`python
# Good: Separate concerns
# data_processor.py
class DataProcessor:
    def process_csv(self, filename):
        # Handle CSV processing
        pass

# file_handler.py
class FileHandler:
    def read_file(self, filename):
        # Handle file operations
        pass

# validator.py
class DataValidator:
    def validate_data(self, data):
        # Handle validation
        pass
\`\`\`

### 2. Configuration Management

\`\`\`python
# config.py
import os

class Config:
    DATABASE_URL = os.getenv('DATABASE_URL', 'sqlite:///default.db')
    SECRET_KEY = os.getenv('SECRET_KEY', 'dev-secret-key')
    DEBUG = os.getenv('DEBUG', 'False').lower() == 'true'

class DevelopmentConfig(Config):
    DEBUG = True
    DATABASE_URL = 'sqlite:///dev.db'

class ProductionConfig(Config):
    DEBUG = False
    DATABASE_URL = os.getenv('DATABASE_URL')
\`\`\`

### 3. Entry Points

\`\`\`python
# main.py
def main():
    """Main entry point of the application"""
    from calculator import Calculator
    
    calc = Calculator()
    print("Calculator started!")
    
    while True:
        try:
            operation = input("Enter operation (or 'quit'): ")
            if operation.lower() == 'quit':
                break
            # Process operation
        except KeyboardInterrupt:
            print("\\nGoodbye!")
            break

if __name__ == "__main__":
    main()
\`\`\`

## Documentation and Comments

### Module Documentation

\`\`\`python
"""
Math Utilities Module

This module provides common mathematical operations and constants
for use throughout the application.

Author: Your Name
Version: 1.0.0
"""

def calculate_circle_area(radius):
    """
    Calculate the area of a circle.
    
    Args:
        radius (float): The radius of the circle
        
    Returns:
        float: The area of the circle
        
    Raises:
        ValueError: If radius is negative
        
    Example:
        >>> calculate_circle_area(5)
        78.54
    """
    if radius < 0:
        raise ValueError("Radius cannot be negative")
    return 3.14159 * radius ** 2
\`\`\`

## Best Practices

1. **Use meaningful names** for modules, functions, and variables
2. **Keep modules focused** on a single responsibility
3. **Use virtual environments** for project isolation
4. **Document your code** with docstrings and comments
5. **Follow PEP 8** style guidelines
6. **Use version control** (Git) from the start
7. **Write tests** for your code
8. **Manage dependencies** explicitly

## Practice Exercise

Create a personal finance tracker project with proper organization including modules for transactions, categories, reports, and a main CLI interface.
`,
  duration: "60 minutes",
  objectives: [
    "Understand Python modules and packages",
    "Organize projects with proper structure",
    "Manage dependencies with virtual environments",
    "Apply code organization best practices",
    "Document code effectively"
  ],
  order: 8
};

// Export all expanded essentials modules
export const PYTHON_ESSENTIALS_EXPANDED_MODULES = [
  PYTHON_EXCEPTION_HANDLING_MODULE,
  PYTHON_FILE_OPERATIONS_MODULE,
  PYTHON_PROJECT_ORGANIZATION_MODULE
];
